import React from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  TablePagination,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import { WalteroCard, WalteroTypography, WalteroDataTable } from '../waltero';

/**
 * API Usage Users List Component
 * Displays list of users with their API usage statistics
 */
const ApiUsageUsersList = ({ data, searchTerm, onSearchChange, onUserSelect, onPageChange }) => {
  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <WalteroTypography variant="body1" color="textSecondary">
          Loading users data...
        </WalteroTypography>
      </Box>
    );
  }

  const columns = [
    {
      id: 'name',
      label: 'User',
      flex: 1,
      render: (value, row) => (
        <Box>
          <WalteroTypography variant="body2" fontWeight="bold">
            {value}
          </WalteroTypography>
          <WalteroTypography variant="caption" color="textSecondary">
            {row.email}
          </WalteroTypography>
        </Box>
      )
    },
    {
      id: 'organizations',
      label: 'Organizations',
      flex: 1,
      render: (value) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {value?.map((org, index) => (
            <Chip key={index} label={org} size="small" variant="outlined" />
          )) || '-'}
        </Box>
      )
    },
    {
      id: 'total_calls',
      label: 'Total Calls',
      width: 120,
      align: 'right',
      type: 'number',
      render: (value) => (
        <WalteroTypography variant="body2" fontWeight="bold">
          {new Intl.NumberFormat().format(value)}
        </WalteroTypography>
      )
    },
    {
      id: 'success_rate',
      label: 'Success Rate',
      width: 120,
      align: 'center',
      type: 'number',
      render: (value) => (
        <Chip
          label={`${value}%`}
          size="small"
          color={value >= 95 ? 'success' : value >= 90 ? 'warning' : 'error'}
        />
      )
    },
    {
      id: 'average_response_time',
      label: 'Avg Response',
      width: 120,
      align: 'right',
      type: 'number',
      render: (value) => (
        <WalteroTypography variant="body2">
          {value}ms
        </WalteroTypography>
      )
    },
    {
      id: 'last_login',
      label: 'Last Active',
      width: 150,
      render: (value) => (
        <WalteroTypography variant="caption">
          {value ? new Date(value).toLocaleDateString() : 'Never'}
        </WalteroTypography>
      )
    },
    {
      id: 'actions',
      label: 'Actions',
      width: 80,
      align: 'center',
      sortable: false,
      render: (value, row) => (
        <Tooltip title="View Details">
          <IconButton size="small" onClick={() => onUserSelect(row.userId)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <WalteroCard>
        <Box sx={{ p: 2 }}>
          {/* Search Bar */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Box>

          {/* Summary Stats */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <WalteroCard variant="outlined" sx={{ p: 2, flex: 1 }}>
              <WalteroTypography variant="caption" color="textSecondary">
                Total Users
              </WalteroTypography>
              <WalteroTypography variant="h6">
                {data.summary?.totalUsers || 0}
              </WalteroTypography>
            </WalteroCard>
            <WalteroCard variant="outlined" sx={{ p: 2, flex: 1 }}>
              <WalteroTypography variant="caption" color="textSecondary">
                Active Users
              </WalteroTypography>
              <WalteroTypography variant="h6">
                {data.summary?.activeUsers || 0}
              </WalteroTypography>
            </WalteroCard>
            <WalteroCard variant="outlined" sx={{ p: 2, flex: 1 }}>
              <WalteroTypography variant="caption" color="textSecondary">
                Total API Calls
              </WalteroTypography>
              <WalteroTypography variant="h6">
                {new Intl.NumberFormat().format(data.summary?.totalCalls || 0)}
              </WalteroTypography>
            </WalteroCard>
          </Box>

          {/* Data Table */}
          <WalteroDataTable
            data={data.data || []}
            columns={columns}
            sortable={true}
            filterable={false}
            showControls={false}
          />

          {/* Pagination */}
          {data.pagination && (
            <TablePagination
              component="div"
              count={data.pagination.total}
              page={data.pagination.page - 1}
              onPageChange={(e, newPage) => onPageChange(newPage + 1)}
              rowsPerPage={data.pagination.limit}
              rowsPerPageOptions={[]}
            />
          )}
        </Box>
      </WalteroCard>
    </Box>
  );
};

export default ApiUsageUsersList;